<template>
  <div class="UIIcon-wrapper">
    <Component :is="iconComponent" :key="name" class="UIIcon" :title="name" />
  </div>
</template>

<script lang="ts" setup>
import type { PropType } from 'vue'
import { computed, defineAsyncComponent } from 'vue'

import { toPascalCase } from '@/utils'

import type { IIcons } from './types'

const props = defineProps({
  name: { type: String as PropType<IIcons>, required: true },
})

// Rebuild the async wrapper per `name` (a single `defineAsyncComponent` caches
// its first-resolved icon and never updates when `name` changes). Chunk-load
// failures (e.g. a stale-shell icon 404 after a deploy) are healed globally by
// the `vite:preloadError` listener registered in `main.ts` — a per-icon
// `onError` here would be dead code, since that listener calls `preventDefault()`
// and the import resolves rather than rejecting.
const iconComponent = computed(() => {
  const fileBase = toPascalCase(`icon ${props.name}`)
  return defineAsyncComponent(() => import(`./components/${fileBase}.vue`))
})
</script>

<style scoped lang="scss">
.UIIcon-wrapper {
  --size: 16px;
  min-height: var(--size);
  min-width: var(--size);
  display: flex;
  align-items: center;
  justify-content: center;
}
.UIIcon {
  height: var(--size);
  width: var(--size);
  min-height: var(--size);
  min-width: var(--size);
  display: flex;
  fill: var(--color);
}
</style>
